// One definition of the channel taxonomy, shared by the write path (signal
// payloads) and the read path (backend generates its SQL from these constants).

// Order IS precedence order. Both evaluators walk it top-down, so moving an
// entry changes classification. See classifyAcquisitionChannel.
export const ACQUISITION_CHANNELS = [
  'paid_search',
  'paid_other',
  'email',
  'ai_assistant',
  'organic_search',
  'social',
  'referral',
  'direct',
] as const;
export type AcquisitionChannel = (typeof ACQUISITION_CHANNELS)[number];

export const ACQUISITION_CHANNEL_LABELS: Record<AcquisitionChannel, string> = {
  paid_search: 'Paid Search',
  paid_other: 'Paid Other',
  email: 'Email',
  ai_assistant: 'AI Assistant',
  organic_search: 'Organic Search',
  social: 'Social',
  referral: 'Referral',
  direct: 'Direct',
};

// Hostnames a vendor published as observed AI referrers (Plausible, own traffic).
export const AI_ASSISTANT_HOSTS_SOURCED = [
  'chatgpt.com',
  'chat.openai.com',
  'perplexity.ai',
  'claude.ai',
  'phind.com',
] as const;

// Guessed from product names. GA4 and Matomo name the products but publish no
// host list, so confirm each against a real referrer before trusting a 0 count.
export const AI_ASSISTANT_HOSTS_INFERRED = [
  'gemini.google.com',
  'copilot.microsoft.com',
  'deepseek.com',
  'grok.com',
  'x.ai',
  'you.com',
  'poe.com',
] as const;

export const AI_ASSISTANT_HOSTS = [
  ...AI_ASSISTANT_HOSTS_SOURCED,
  ...AI_ASSISTANT_HOSTS_INFERRED,
] as const;

// Google's search front-ends, from https://www.google.com/supported_domains
// (as packaged in is-google-domain@1.0.0). Listed one by one because the
// matcher must also be expressible in ClickHouse SQL, where a public-suffix
// lookup is not available.
const GOOGLE_SEARCH_TLDS = [
  'com',
  'ad',
  'ae',
  'com.af',
  'com.ag',
  'com.ai',
  'al',
  'am',
  'co.ao',
  'com.ar',
  'as',
  'at',
  'com.au',
  'az',
  'ba',
  'com.bd',
  'be',
  'bf',
  'bg',
  'com.bh',
  'bi',
  'bj',
  'com.bn',
  'com.bo',
  'com.br',
  'bs',
  'bt',
  'co.bw',
  'by',
  'com.bz',
  'ca',
  'cd',
  'cf',
  'cg',
  'ch',
  'ci',
  'co.ck',
  'cl',
  'cm',
  'cn',
  'com.co',
  'co.cr',
  'com.cu',
  'cv',
  'com.cy',
  'cz',
  'de',
  'dj',
  'dk',
  'dm',
  'com.do',
  'dz',
  'com.ec',
  'ee',
  'com.eg',
  'es',
  'com.et',
  'fi',
  'com.fj',
  'fm',
  'fr',
  'ga',
  'ge',
  'gg',
  'com.gh',
  'com.gi',
  'gl',
  'gm',
  'gp',
  'gr',
  'com.gt',
  'gy',
  'com.hk',
  'hn',
  'hr',
  'ht',
  'hu',
  'co.id',
  'ie',
  'co.il',
  'im',
  'co.in',
  'iq',
  'is',
  'it',
  'je',
  'com.jm',
  'jo',
  'co.jp',
  'co.ke',
  'com.kh',
  'ki',
  'kg',
  'co.kr',
  'com.kw',
  'kz',
  'la',
  'com.lb',
  'li',
  'lk',
  'co.ls',
  'lt',
  'lu',
  'lv',
  'com.ly',
  'co.ma',
  'md',
  'me',
  'mg',
  'mk',
  'ml',
  'com.mm',
  'mn',
  'ms',
  'com.mt',
  'mu',
  'mv',
  'mw',
  'com.mx',
  'com.my',
  'co.mz',
  'com.na',
  'com.nf',
  'com.ng',
  'com.ni',
  'ne',
  'nl',
  'no',
  'com.np',
  'nr',
  'nu',
  'co.nz',
  'com.om',
  'com.pa',
  'com.pe',
  'com.pg',
  'com.ph',
  'com.pk',
  'pl',
  'pn',
  'com.pr',
  'ps',
  'pt',
  'com.py',
  'com.qa',
  'ro',
  'ru',
  'rw',
  'com.sa',
  'com.sb',
  'sc',
  'se',
  'com.sg',
  'sh',
  'si',
  'sk',
  'com.sl',
  'sn',
  'so',
  'sm',
  'sr',
  'st',
  'com.sv',
  'td',
  'tg',
  'co.th',
  'com.tj',
  'tk',
  'tl',
  'tm',
  'tn',
  'to',
  'com.tr',
  'tt',
  'com.tw',
  'co.tz',
  'com.ua',
  'co.ug',
  'co.uk',
  'com.uy',
  'co.uz',
  'com.vc',
  'co.ve',
  'vg',
  'co.vi',
  'com.vn',
  'vu',
  'ws',
  'rs',
  'co.za',
  'co.zm',
  'co.zw',
  'cat',
] as const;

// Matched EXACTLY, never by subdomain: mail., accounts., docs. and the other
// google.com subdomains are not search, and gemini.google.com is an assistant.
// Search Console covers Google only, so its report joins use this list alone.
export const GOOGLE_SEARCH_HOSTS: readonly string[] = GOOGLE_SEARCH_TLDS.map(
  (tld) => `google.${tld}`,
);

// Matched exactly or by subdomain (cn.bing.com, html.duckduckgo.com).
export const OTHER_SEARCH_HOSTS = [
  'bing.com',
  'duckduckgo.com',
  'search.yahoo.com',
  'yahoo.com',
  'ecosia.org',
  'startpage.com',
  'search.brave.com',
  'baidu.com',
  'yandex.com',
] as const;

export const SEARCH_HOSTS: readonly string[] = [
  ...GOOGLE_SEARCH_HOSTS,
  ...OTHER_SEARCH_HOSTS,
];

// Webmail front-ends. No vendor publishes these as referrers, so like the
// inferred AI hosts, confirm each against real traffic. com.google.android.gm
// is the Gmail app's `android-app://` referrer.
export const EMAIL_HOSTS = [
  'mail.google.com',
  'com.google.android.gm',
  'mail.yahoo.com',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.aol.com',
  'mail.proton.me',
] as const;

export const SOCIAL_HOSTS = [
  'facebook.com',
  'linkedin.com',
  'lnkd.in',
  't.co',
  'x.com',
  'twitter.com',
  'reddit.com',
  'instagram.com',
  'youtube.com',
  'tiktok.com',
  'news.ycombinator.com',
  'youtu.be',
  'threads.net',
  'threads.com',
  'bsky.app',
  'pinterest.com',
  'mastodon.social',
] as const;

// utm_source arrives as a hostname ('chatgpt.com') or a bare token ('google').
export const AI_ASSISTANT_SOURCE_TOKENS = [
  'chatgpt',
  'openai',
  'perplexity',
  'claude',
  'gemini',
  'copilot',
  'deepseek',
  'grok',
  'phind',
  'poe',
] as const;

export const SEARCH_SOURCE_TOKENS = [
  'google',
  'bing',
  'duckduckgo',
  'yahoo',
  'ecosia',
  'baidu',
  'yandex',
  'brave',
] as const;

export const SOCIAL_SOURCE_TOKENS = [
  'facebook',
  'linkedin',
  'twitter',
  'reddit',
  'instagram',
  'youtube',
  'tiktok',
  'x',
  'threads',
  'bluesky',
  'pinterest',
  'mastodon',
] as const;

// GA4's paid regex is `^(.*cp.*|ppc|retargeting|paid.*)$`. Listed explicitly
// because a regex that loose matches any medium merely containing "cp".
export const PAID_MEDIUMS = [
  'cpc',
  'ppc',
  'cpm',
  'cpv',
  'cpa',
  'paid',
  'paidsearch',
  'paid_search',
  'retargeting',
] as const;

// Paid placements outside search engines (social ads, display, sponsorships).
// Display tokens follow GA4's Display channel; any PAID_MEDIUMS value from a
// non-search source is paid too.
export const PAID_OTHER_MEDIUMS = [
  'paid_social',
  'paidsocial',
  'paid-social',
  'display',
  'banner',
  'sponsored',
] as const;

export const EMAIL_TOKENS = [
  'email',
  'e-mail',
  'e_mail',
  'e mail',
  'newsletter',
] as const;

export const ORGANIC_MEDIUM = 'organic';

export const GOOGLE_SOURCE_TOKEN = 'google';

// Auto-tagged ad clicks carry only these landing-URL params (on by default,
// per https://support.google.com/google-ads/answer/3095550), not utm_medium.
export const GOOGLE_ADS_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

// Dotless hosts (`localhost`, intranet names) return null so this matches
// ClickHouse `domainWithoutWWW`, which drops them but keeps bare IPs.
export function normalizeAcquisitionHost(
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const host = withoutScheme.split(/[/?#]/)[0] ?? '';
  const withoutPort = host.split(':')[0] ?? '';
  const bare = withoutPort.replace(/^www\./, '');
  if (!bare || !bare.includes('.')) return null;
  return bare;
}

// utm_source is also matched as a bare brand token ('google'), which is not a
// host, so token comparison keeps its own normalizer.
function normalizeToken(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
}

// Exact host or a true subdomain, never a substring, so `notchatgpt.com` and
// `chatgpt.com.evil.io` cannot read as ChatGPT.
function hostMatches(host: string | null, entries: readonly string[]): boolean {
  if (!host) return false;
  return entries.some((entry) => host === entry || host.endsWith(`.${entry}`));
}

function hostEquals(host: string | null, entries: readonly string[]): boolean {
  return host !== null && entries.includes(host);
}

function tokenMatches(
  value: string | null,
  tokens: readonly string[],
): boolean {
  return value !== null && tokens.includes(value);
}

function normalizeMedium(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
}

// `?ref=` tags a link with its source like utm_source does (`?ref=launch-tweet`),
// so it stands in for utm_source when that is absent.

// Percent-decoding that, like URLSearchParams, keeps malformed sequences as-is.
function decodeFormComponent(value: string): string {
  const spaced = value.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(spaced);
  } catch {
    return spaced.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
      try {
        return decodeURIComponent(run);
      } catch {
        return run;
      }
    });
  }
}

/** The `ref` query value of a url (absolute or path), form-decoded. */
export function refFromUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const queryStart = url.indexOf('?');
  if (queryStart < 0) return null;
  const fragmentStart = url.indexOf('#', queryStart);
  const query = url.slice(
    queryStart + 1,
    fragmentStart < 0 ? undefined : fragmentStart,
  );
  const pair = query.split('&').find((part) => part.startsWith('ref='));
  if (pair === undefined) return null;
  return decodeFormComponent(pair.slice('ref='.length)).trim() || null;
}

export type AcquisitionChannelInput = {
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  /** `?ref=` value; used only when utm_source is empty. */
  ref?: string | null;
};

/** Derive the acquisition channel. Pure and total. */
export function classifyAcquisitionChannel(
  input: AcquisitionChannelInput,
): AcquisitionChannel {
  // utm_source is matched as well as the referrer, because ChatGPT stamps
  // `utm_source=chatgpt.com` and AI referrals often carry no referrer at all.
  const source = normalizeToken(input.utmSource) ? input.utmSource : input.ref;
  const referrerHost = normalizeAcquisitionHost(input.referrer);
  const sourceHost = normalizeAcquisitionHost(source);
  const sourceToken = normalizeToken(source);
  const medium = normalizeMedium(input.utmMedium);

  const isSearch =
    hostEquals(referrerHost, GOOGLE_SEARCH_HOSTS) ||
    hostEquals(sourceHost, GOOGLE_SEARCH_HOSTS) ||
    hostMatches(referrerHost, OTHER_SEARCH_HOSTS) ||
    hostMatches(sourceHost, OTHER_SEARCH_HOSTS) ||
    tokenMatches(sourceToken, SEARCH_SOURCE_TOKENS);

  // Paid MUST precede organic: GA4's organic rule is a superset of its paid
  // rule, so organic-first swallows ad clicks and reports paid traffic as free.
  if (isSearch && tokenMatches(medium, PAID_MEDIUMS)) return 'paid_search';

  if (
    tokenMatches(medium, PAID_MEDIUMS) ||
    tokenMatches(medium, PAID_OTHER_MEDIUMS)
  ) {
    return 'paid_other';
  }

  if (
    tokenMatches(medium, EMAIL_TOKENS) ||
    tokenMatches(sourceToken, EMAIL_TOKENS) ||
    hostMatches(referrerHost, EMAIL_HOSTS) ||
    hostMatches(sourceHost, EMAIL_HOSTS)
  ) {
    return 'email';
  }

  if (
    hostMatches(referrerHost, AI_ASSISTANT_HOSTS) ||
    hostMatches(sourceHost, AI_ASSISTANT_HOSTS) ||
    tokenMatches(sourceToken, AI_ASSISTANT_SOURCE_TOKENS)
  ) {
    return 'ai_assistant';
  }

  if (isSearch || medium === ORGANIC_MEDIUM) return 'organic_search';

  if (
    hostMatches(referrerHost, SOCIAL_HOSTS) ||
    hostMatches(sourceHost, SOCIAL_HOSTS) ||
    tokenMatches(sourceToken, SOCIAL_SOURCE_TOKENS)
  ) {
    return 'social';
  }

  // A tagged link (`?ref=launch-tweet`) is a referral even without a referrer.
  if (referrerHost || sourceToken) return 'referral';

  return 'direct';
}

export function acquisitionChannelLabel(channel: string): string {
  return ACQUISITION_CHANNEL_LABELS[channel as AcquisitionChannel] ?? 'Unknown';
}
